import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Seo from '../components/Seo';
import ScrollReveal from '../components/ScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import { countryPages, countrySlugs, CountrySlug } from '../data/countryPages';
import { consultationWhatsAppUrl } from '../data/whatsapp';
import './CountryPage.css';

interface CountryPageProps {
  slug: CountrySlug;
}

const WHATSAPP_URL = consultationWhatsAppUrl();

const CountryPage: React.FC<CountryPageProps> = ({ slug }) => {
  const { language } = useLanguage();
  const content = countryPages[slug]?.[language];
  const actionsRef = useRef<HTMLDivElement>(null);
  const [shimmer, setShimmer] = useState(false);

  useEffect(() => {
    const node = actionsRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShimmer(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [slug]);

  if (!content) {
    return <Navigate to="/" replace />;
  }

  const path = `/${slug}`;
  const related = countrySlugs.filter((item) => item !== slug);
  const copy = {
    ar: {
      home: 'الشراع',
      related: 'دول أخرى نخدمها',
      quote: 'اطلب عرض سعر',
      contact: 'نموذج التواصل',
    },
    en: {
      home: 'Al Shiraa',
      related: 'Other countries we serve',
      quote: 'Request a quote',
      contact: 'Contact form',
    },
    de: {
      home: 'Al Shiraa',
      related: 'Weitere Länder',
      quote: 'Angebot anfordern',
      contact: 'Kontaktformular',
    },
  }[language];

  return (
    <article className="country-page">
      <Seo
        title={content.title}
        description={content.description}
        path={path}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: content.h1,
          serviceType: 'Customs clearance',
          areaServed: content.name,
          provider: {
            '@type': 'LocalBusiness',
            name: 'شركة الشراع للتخليص الجمركي',
            url: 'https://alshiraa-customs.com/',
            telephone: '+963933612861',
          },
          url: `https://alshiraa-customs.com${path}`,
          description: content.description,
        }}
      />
      <div className="container country-page-inner">
        <div className="country-actions country-actions-top" ref={actionsRef}>
          <a
            className={`btn btn-primary btn-shimmer ${shimmer ? 'is-shimmering' : ''}`}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp
          </a>
          <Link className={`btn btn-secondary btn-shimmer ${shimmer ? 'is-shimmering' : ''}`} to="/#quick-quote">
            {copy.quote}
          </Link>
        </div>
        <p className="country-breadcrumb">
          <Link to="/">{copy.home}</Link>
          <span> / </span>
          <span>{content.name}</span>
        </p>
        <h1 className="country-heading">{content.h1}</h1>
        <p className="country-intro">{content.intro}</p>
        {content.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        <h2>{content.crossingsTitle}</h2>
        <ScrollReveal variant="fade-up" asStagger>
          <ul className="country-crossings">
            {content.crossings.map((item, index) => (
              <li
                key={item}
                className="stagger-item"
                style={{ transitionDelay: `${index * 70}ms` }}
              >
                <span className="country-list-dot" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </ScrollReveal>
        <div className="country-actions">
          <a
            className={`btn btn-primary btn-shimmer ${shimmer ? 'is-shimmering' : ''}`}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp — {content.cta}
          </a>
          <Link className="btn btn-secondary" to="/#quick-quote">
            {copy.quote}
          </Link>
          <Link className="btn btn-secondary" to="/#contact">
            {copy.contact}
          </Link>
        </div>
        <h2>{copy.related}</h2>
        <ScrollReveal variant="fade-scale" asStagger>
          <div className="country-related">
            {related.map((item, index) => (
              <Link
                key={item}
                className="country-related-card interactive-lift stagger-item"
                to={`/${item}`}
                style={{ transitionDelay: `${index * 55}ms` }}
              >
                {countryPages[item][language].name}
              </Link>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </article>
  );
};

export default CountryPage;
